import { css } from 'styled-components';
import Colr from 'colr';
import { buttonReset, getSmallButtonTokens, getMediumButtonTokens, getLargeButtonTokens, getPrimaryButtonTokens, getPrimaryButtonSelectedTokens, getPrimaryButtonDisabledTokens, getSecondaryButtonTokens, getSecondaryButtonSelectedTokens, getSecondaryButtonDisabledTokens, getTertiaryButtonTokens, getTertiaryButtonSelectedTokens, getTertiaryButtonDisabledTokens, getDestructiveButtonTokens, getDestructiveButtonSelectedTokens, getDestructiveButtonDisabledTokens } from '../shared';
import common, { spacing } from '../../common';
import token from '../../getToken';
export const hoverColor = color => Colr.fromHex(color).darken(10).toHex();
export const activeColor = color => Colr.fromHex(color).darken(20).toHex();
export const disabledColor = (color, percent) => {
  const opacity = percent || 0.3;
  const rgb = Colr.fromHex(color).toRgbArray().toString();
  return `rgba(${rgb}, ${opacity})`;
};
const typeScale = {
  1: '2.986rem',
  2: '2.488rem',
  3: '2.074rem',
  4: '1.728rem',
  5: '1.44rem',
  6: '1.21rem',
  7: '1rem',
  8: '0.875rem'
};
const lineHeight = [1, 1.2, 1.5];
const tracking = {
  normal: 'normal'
};
const fonts = {
  nunito: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Arial Nova', Helvetica, Arial, sans-serif"
};
const cyan = {
  base: '#00b4f1',
  dark25: '#000b0f',
  dark50: '#00131a',
  dark100: '#001f29',
  dark150: '#002a38',
  dark200: '#003547',
  dark250: '#004156',
  dark300: '#004d67',
  dark375: '#005f80',
  dark450: '#00739a',
  light550: '#008ebe',
  light625: '#00a2d9',
  light687: '#00b4f1',
  light750: '#3fc7f4',
  light800: '#68d4f7',
  light850: '#93dff9',
  light900: '#b9eafb',
  light950: '#ddf5fd',
  light975: '#eefafe'
};
const green = {
  base: '#73c166',
  dark25: '#060a05',
  dark50: '#0b130a',
  dark100: '#121f10',
  dark150: '#192a16',
  dark200: '#20361c',
  dark250: '#274223',
  dark300: '#2f4e29',
  dark375: '#3a6234',
  dark450: '#46763e',
  light550: '#57914d',
  light625: '#63a758',
  light713: '#73c166',
  light750: '#85c97a',
  light800: '#9ed495',
  light850: '#b7dfb0',
  light900: '#cfeacb',
  light950: '#e7f4e5',
  light975: '#f3faf2'
};
const colors = {
  brand: {
    base: cyan.light550,
    light: cyan.light687,
    dark: cyan.dark450
  },
  accent: green.base,
  shades: {
    black: '#000',
    darkest: '#262626',
    darker: '#3b3b3b',
    dark: '#777777',
    light: '#b9b9b9',
    lighter: '#d4d4d4',
    lightest: '#e2e2e2',
    nearWhite: '#f8f8f8',
    white: '#ffffff'
  },
  focus: cyan.dark375,
  success: green.light713,
  error: '#f44336',
  info: cyan.dark375,
  product: {
    hospital: cyan.light687,
    extras: cyan.light687,
    combined: cyan.light687,
    highlight: {
      backgroundColor: '#262626',
      textColor: '#f1f1f1'
    }
  }
};
const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '3px',
  outlineOffset: '0.125em'
};
const baseButtonStyles = css`
  ${buttonReset};

  font-family: ${fonts.nunito};
  font-weight: 700;
  letter-spacing: ${tracking.normal};
  border-radius: ${token('button.dimension.border.radius', '999px')};
  border-width: ${token('button.dimension.border.width', '0')};
  border-style: solid;
`;
const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles};
      ${getPrimaryButtonTokens({
      bg: {
        default: green.base,
        hover: green.light800,
        active: green.light625,
        disabled: colors.shades.lightest
      },
      fg: {
        default: cyan.dark50,
        hover: cyan.dark50,
        active: cyan.dark50,
        disabled: colors.shades.light
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      }
    })};
    `,
    disabled: css`
      ${getPrimaryButtonDisabledTokens({
      bg: colors.shades.lightest,
      fg: colors.shades.light,
      border: 'transparent'
    })};
    `,
    selected: css`
      ${getPrimaryButtonSelectedTokens({
      bg: green.light625,
      fg: cyan.dark50,
      border: 'transparent'
    })};
    `
  },
  secondary: {
    normal: css`
      ${baseButtonStyles}
      ${getSecondaryButtonTokens({
      bg: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      },
      fg: {
        default: green.dark450,
        hover: green.dark375,
        active: green.dark300,
        disabled: disabledColor(green.dark450, 0.4)
      },
      border: {
        default: green.dark450,
        hover: green.dark375,
        active: green.dark300,
        disabled: disabledColor(green.dark450, 0.4)
      }
    })};
    `,
    disabled: css`
      ${getSecondaryButtonDisabledTokens({
      bg: 'transparent',
      fg: disabledColor(green.dark450, 0.4),
      border: disabledColor(green.dark450, 0.4)
    })};
      cursor: not-allowed;
    `,
    selected: css`
      ${getSecondaryButtonSelectedTokens({
      bg: 'transparent',
      fg: green.dark300,
      border: green.dark300
    })};
    `
  },
  tertiary: {
    normal: css`
      ${baseButtonStyles};
      ${getTertiaryButtonTokens({
      bg: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      },
      fg: {
        default: green.dark450,
        hover: green.dark375,
        active: green.dark300,
        disabled: disabledColor(green.dark450, 0.4)
      },
      border: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      }
    })};
    `,
    disabled: css`
      ${getTertiaryButtonDisabledTokens({
      bg: 'transparent',
      fg: disabledColor(green.dark450, 0.4),
      border: 'transparent'
    })};
    `,
    selected: css`
      ${getTertiaryButtonSelectedTokens({
      bg: 'transparent',
      fg: green.dark300,
      border: 'transparent'
    })};
    `
  },
  destructive: {
    normal: css`
      ${baseButtonStyles};
      ${getDestructiveButtonTokens({
      bg: {
        default: 'transparent',
        hover: 'transparent',
        active: 'transparent',
        disabled: 'transparent'
      },
      fg: {
        default: colors.error,
        hover: hoverColor(colors.error),
        active: activeColor(colors.error),
        disabled: disabledColor(colors.error, 0.4)
      },
      border: {
        default: colors.error,
        hover: hoverColor(colors.error),
        active: activeColor(colors.error),
        disabled: disabledColor(colors.error, 0.4)
      }
    })};
    `,
    disabled: css`
      ${getDestructiveButtonDisabledTokens({
      bg: 'transparent',
      fg: disabledColor(colors.error, 0.4),
      border: disabledColor(colors.error, 0.4)
    })};
    `,
    selected: css`
      ${getDestructiveButtonSelectedTokens({
      bg: 'transparent',
      fg: activeColor(colors.error),
      border: activeColor(colors.error)
    })};
    `
  },
  small: css`
    ${getSmallButtonTokens({
    fontFamily: fonts.nunito,
    fontSize: typeScale[8],
    fontWeight: 700,
    lineHeight: lineHeight[0],
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[3],
    paddingInline: spacing[3],
    gap: spacing[2]
  })};
  `,
  medium: css`
    ${getMediumButtonTokens({
    fontFamily: fonts.nunito,
    fontSize: typeScale[7],
    fontWeight: 700,
    lineHeight: lineHeight[0],
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[4],
    paddingInline: spacing[6],
    gap: spacing[2]
  })};
  `,
  large: css`
    ${getLargeButtonTokens({
    fontFamily: fonts.nunito,
    fontSize: typeScale[6],
    fontWeight: 700,
    lineHeight: lineHeight[0],
    letterSpacing: tracking.normal,
    textTransform: 'none',
    paddingBlock: spacing[4],
    paddingInline: spacing[6],
    gap: spacing[2]
  })};
  `
};
const baseFormValues = {
  borderColor: colors.shades.dark,
  borderColorValid: colors.success,
  borderColorInvalid: colors.error,
  selectedToggleColor: colors.brand.base,
  helpTextColor: colors.shades.darker,
  helpTextSize: typeScale[8],
  labelColor: colors.shades.darkest,
  labelSize: typeScale[7],
  errorMessageTextColor: colors.error,
  infoMessageTextColor: colors.info
};
const orbitProtect = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,
  id: 'orbitProtect',
  mode: {
    form: 'white' //default
  },
  headings: {
    color: 'currentColor',
    h1: {
      fontFamily: fonts.nunito,
      fontSize: typeScale[1],
      lineHeight: lineHeight[0],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.nunito,
      fontSize: typeScale[2],
      lineHeight: lineHeight[0],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.nunito,
      fontSize: typeScale[3],
      lineHeight: lineHeight[0],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.nunito,
      fontSize: typeScale[4],
      lineHeight: lineHeight[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.nunito,
      fontSize: typeScale[5],
      lineHeight: lineHeight[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.nunito,
      fontSize: typeScale[6],
      lineHeight: lineHeight[1],
      fontWeight: 700,
      tracking: tracking.normal,
      uppercase: false
    }
  },
  copy: {
    color: colors.shades.darkest,
    fontFamily: fonts.nunito,
    fontSize: typeScale[7],
    fontWeight: 400,
    lineHeight: lineHeight[2],
    smallFontSize: typeScale[8],
    largeFontSize: typeScale[5]
  },
  link: {
    loudColor: colors.brand.dark,
    darkColor: colors.shades.darkest,
    lightColor: colors.shades.white,
    destructiveColor: colors.error
  },
  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: buttonStyles.primary,
    secondary: buttonStyles.secondary,
    tertiary: buttonStyles.tertiary,
    destructive: buttonStyles.destructive
  },
  smallFooter: {
    backgroundColor: colors.shades.white,
    textColor: colors.shades.darkest,
    linkColor: colors.shades.darkest
  },
  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  }
};
export default orbitProtect;